import { useState } from "react"
import { Head, useForm } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Radio, RadioGroup } from "@/components/ui/radio"

type Settings = {
  google_analytics_code:             string | null
  google_adsense_code:               string | null
  facebook_pixel_code:               string | null
  trivia_ads_enabled:                string | null
  donation_annual_threshold_cents:   string | null
  donation_lifetime_threshold_cents: string | null
  daily_roundup_publish_time:        string | null
  daily_roundup_max_births:          string | null
  daily_roundup_max_deaths:          string | null
  daily_roundup_max_first_charted:   string | null
  daily_roundup_max_chart_listings:  string | null
  daily_roundup_max_gigs:            string | null
}

type RoundupDefaults = Record<string, number>

type Props = { settings: Settings; roundup_defaults: RoundupDefaults }

const CODE_FIELDS: Array<{ key: keyof Settings; label: string; hint: string }> = [
  {
    key:   "google_analytics_code",
    label: "Google Analytics (GA4)",
    hint:  "Paste the full <script>…</script> tag block from your GA4 property. Only injected in production.",
  },
  {
    key:   "google_adsense_code",
    label: "Google AdSense auto-ads",
    hint:  "Paste the AdSense auto-ads <script>…</script> block. Suppressed automatically for users with ad-free access.",
  },
  {
    key:   "facebook_pixel_code",
    label: "Facebook Pixel",
    hint:  "Paste the full Facebook Pixel <script>…</script> and <noscript>…</noscript> block. Only injected in production.",
  },
]

const MAX_ENTRY_FIELDS: Array<{ key: keyof Settings; label: string }> = [
  { key: "daily_roundup_max_births",          label: "Born Today" },
  { key: "daily_roundup_max_deaths",          label: "Passing Today" },
  { key: "daily_roundup_max_first_charted",   label: "First Charting Today" },
  { key: "daily_roundup_max_chart_listings",  label: "In The Charts Today" },
  { key: "daily_roundup_max_gigs",            label: "Gigs on this day" },
]

function centsToDisplay(value: string | null): string {
  const n = parseInt(value ?? "", 10)
  return isNaN(n) ? "" : String(Math.round(n / 100))
}

function displayToCents(display: string): string {
  const n = parseInt(display, 10)
  return isNaN(n) ? "" : String(n * 100)
}

function MaxEntriesField({
  id,
  label,
  value,
  defaultMax,
  onChange,
}: {
  id: string
  label: string
  value: string
  defaultMax: number
  onChange: (value: string) => void
}) {
  const [mode, setMode] = useState<"default" | "limit">(value ? "limit" : "default")

  return (
    <div className="space-y-2">
      <span className="block font-medium text-sm">{label}</span>
      <RadioGroup orientation="horizontal" className="items-center">
        <label className="flex items-center gap-2 text-sm">
          <Radio
            name={`${id}_mode`}
            checked={mode === "default"}
            onChange={() => {
              setMode("default")
              onChange("")
            }}
          />
          Default ({defaultMax})
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Radio
            name={`${id}_mode`}
            checked={mode === "limit"}
            onChange={() => setMode("limit")}
          />
          Limit to
        </label>
        <Input
          id={id}
          type="number"
          min="1"
          step="1"
          className="w-24"
          disabled={mode === "default"}
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      </RadioGroup>
    </div>
  )
}

export default function SiteSettings({ settings, roundup_defaults }: Props) {
  const { data, setData, patch, processing, recentlySuccessful } = useForm<Settings>({
    google_analytics_code:             settings.google_analytics_code             ?? "",
    google_adsense_code:               settings.google_adsense_code               ?? "",
    facebook_pixel_code:               settings.facebook_pixel_code               ?? "",
    trivia_ads_enabled:                settings.trivia_ads_enabled                ?? "",
    donation_annual_threshold_cents:   settings.donation_annual_threshold_cents   ?? "",
    donation_lifetime_threshold_cents: settings.donation_lifetime_threshold_cents ?? "",
    daily_roundup_publish_time:        settings.daily_roundup_publish_time        ?? "",
    daily_roundup_max_births:          settings.daily_roundup_max_births          ?? "",
    daily_roundup_max_deaths:          settings.daily_roundup_max_deaths          ?? "",
    daily_roundup_max_first_charted:   settings.daily_roundup_max_first_charted   ?? "",
    daily_roundup_max_chart_listings:  settings.daily_roundup_max_chart_listings  ?? "",
    daily_roundup_max_gigs:            settings.daily_roundup_max_gigs            ?? "",
  })

  // Separate display state so typing isn't interrupted by cents conversion on each keystroke
  const [annualDisplay, setAnnualDisplay] = useState(() => centsToDisplay(settings.donation_annual_threshold_cents))
  const [lifetimeDisplay, setLifetimeDisplay] = useState(() => centsToDisplay(settings.donation_lifetime_threshold_cents))

  function submit(e: React.FormEvent) {
    e.preventDefault()
    patch("/admin/site_settings", { preserveScroll: true })
  }

  return (
    <>
      <Head title="Site Settings — Admin" />
      <AdminShell>
        <div className="py-6 space-y-12">
          <section>
            <h1 className="mb-1">Site Settings</h1>
          </section>

          <form onSubmit={submit} className="space-y-12">

            {/* ── Daily roundup post ───────────────────────────────────── */}
            <section className="space-y-6">
              <div>
                <h2 className="mb-1">Daily Roundup Post</h2>
                <p className="text-sm text-stone-500 dark:text-stone-400">
                  Controls the automated "On this day in Aussie Music" post to Facebook and X.
                  Changes here take effect within 5 minutes — no deploy needed.
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="daily_roundup_publish_time" className="block font-medium text-sm">
                  Publish time (Australia/Sydney)
                </label>
                <Input
                  id="daily_roundup_publish_time"
                  type="time"
                  className="w-40"
                  value={data.daily_roundup_publish_time ?? ""}
                  onChange={e => setData("daily_roundup_publish_time", e.target.value)}
                />
                <p className="text-xs text-stone-400 dark:text-stone-500">
                  Leave blank to use the default of 9:00am.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <span className="block font-medium text-sm">Entries per section</span>
                  <p className="text-xs text-stone-400 dark:text-stone-500">
                    When a section has more entries than its limit, the ones included are chosen at random
                    (rather than always the same earliest entries). Every section is capped so a busy date
                    can't produce an over-long post — Facebook hides anything past roughly 400 characters
                    behind &ldquo;See more&rdquo;.
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  {MAX_ENTRY_FIELDS.map(({ key, label }) => (
                    <MaxEntriesField
                      key={key}
                      id={key}
                      label={label}
                      value={data[key] ?? ""}
                      defaultMax={roundup_defaults[key]}
                      onChange={value => setData(key, value)}
                    />
                  ))}
                </div>
              </div>
            </section>

            {/* ── Donation amounts ─────────────────────────────────────── */}
            <section className="space-y-6">
              <div>
                <h2 className="mb-1">Donation amounts</h2>
                <p className="text-sm text-stone-500 dark:text-stone-400">
                  Set the minimum amount for each tier. Leave blank to use the
                  environment variable default ($25 annual / $150 lifetime).
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="annual_amount" className="block font-medium text-sm">
                    Annual ad-free (AUD)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm select-none">$</span>
                    <Input
                      id="annual_amount"
                      type="number"
                      min="1"
                      step="1"
                      className="pl-7"
                      placeholder="25"
                      value={annualDisplay}
                      onChange={e => {
                        setAnnualDisplay(e.target.value)
                        setData("donation_annual_threshold_cents", displayToCents(e.target.value))
                      }}
                    />
                  </div>
                  <p className="text-xs text-stone-400 dark:text-stone-500">
                    Grants ad-free access for one year
                  </p>
                </div>

                <div className="space-y-2">
                  <label htmlFor="lifetime_amount" className="block font-medium text-sm">
                    Lifetime ad-free (AUD)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm select-none">$</span>
                    <Input
                      id="lifetime_amount"
                      type="number"
                      min="1"
                      step="1"
                      className="pl-7"
                      placeholder="150"
                      value={lifetimeDisplay}
                      onChange={e => {
                        setLifetimeDisplay(e.target.value)
                        setData("donation_lifetime_threshold_cents", displayToCents(e.target.value))
                      }}
                    />
                  </div>
                  <p className="text-xs text-stone-400 dark:text-stone-500">
                    Grants permanent ad-free access
                  </p>
                </div>
              </div>
            </section>

            {/* ── Code snippets ─────────────────────────────────────────── */}
            <section className="space-y-6">
              <div>
                <h2 className="mb-1">Analytics &amp; advertising</h2>
                <p className="text-sm text-stone-500 dark:text-stone-400">
                  Injected into{" "}
                  <code className="text-xs bg-stone-100 dark:bg-stone-800 px-1 py-0.5 rounded">&lt;head&gt;</code>{" "}
                  in production only. Save empty to disable.
                </p>
              </div>

              {CODE_FIELDS.map(({ key, label, hint }) => (
                <div key={key} className="space-y-2">
                  <label htmlFor={key} className="block font-medium text-sm">{label}</label>
                  <p className="text-xs text-stone-500 dark:text-stone-400">{hint}</p>
                  <textarea
                    id={key}
                    rows={6}
                    value={data[key] ?? ""}
                    onChange={e => setData(key, e.target.value)}
                    className="w-full font-mono text-xs rounded-lg border border-hairline bg-surface px-3 py-2 text-ink-body placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-primary resize-y"
                    placeholder={`Paste ${label} code here…`}
                    spellCheck={false}
                  />
                </div>
              ))}

              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  className="mt-0.5"
                  checked={data.trivia_ads_enabled === "true"}
                  onChange={e => setData("trivia_ads_enabled", e.target.checked ? "true" : "")}
                />
                <span>
                  <span className="font-medium">Show ads on the trivia quiz, leaderboard &amp; wall of fame</span>
                  <p className="text-xs text-stone-500 dark:text-stone-400">
                    Off by default — these pages are ad-free regardless of AdSense being configured above.
                  </p>
                </span>
              </label>
            </section>

            <div className="flex items-center gap-4">
              <Button type="submit" disabled={processing}>Save settings</Button>
              {recentlySuccessful && (
                <span className="text-sm text-green-600 dark:text-green-400">Saved.</span>
              )}
            </div>
          </form>
        </div>
      </AdminShell>
    </>
  )
}
